import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, GitBranch, Plus, Trash2 } from './icons';

const CONDITION_OPERATORS = [
    { key: 'exists', label: 'Exists' },
    { key: 'equals', label: 'Equals' },
    { key: 'yes', label: 'Yes' },
    { key: 'no', label: 'No' },
];

export function RuleModal({ isOpen, onClose, onSave, onRemove, variables = [], initialData, tableTargetOptions }) {
    const [tableScope, setTableScope] = useState('row');
    const [operator, setOperator] = useState('&&');
    const [rules, setRules] = useState([{ field: '', operator: 'exists', value: '' }]);

    const sortedVariables = useMemo(() => {
        return [...variables].sort((a, b) => (a.label || a.key || '').localeCompare(b.label || b.key || '', undefined, { numeric: true, sensitivity: 'base' }));
    }, [variables]);

    const loadRuleData = useCallback((data) => {
        if (data && data.rules && Array.isArray(data.rules) && data.rules.length > 0) {
            const op = data.operator === 'OR' || data.operator === '||' ? '||' : '&&';
            setOperator(op);
            
            const mappedRules = data.rules.map((r) => {
                const rawField = (r.field || r.variable || '').replace(/<[^>]+>/g, '').replace(/<\/?strong[^>]*>/gi, '').replace(/<\/?b[^>]*>/gi, '').replace(/\*\*/g, '').trim();
                const matchedVar = variables.find(v => 
                    v.key === rawField ||
                    (v.label && v.label.toLowerCase() === rawField.toLowerCase()) ||
                    (v.key && v.key.toLowerCase() === rawField.toLowerCase().replace(/[^a-zA-Z0-9]/g, '_'))
                );
                const fieldVal = matchedVar ? matchedVar.key : rawField;
                
                let opVal = (r.operator || 'exists').toLowerCase();
                let rawVal = r.value !== undefined && r.value !== null ? String(r.value).replace(/<[^>]+>/g, '').replace(/<\/?strong[^>]*>/gi, '').replace(/<\/?b[^>]*>/gi, '').replace(/\*\*/g, '').trim() : '';

                if (opVal === 'yes' || ((opVal === 'equals' || opVal === '==' || opVal === '===') && rawVal.toLowerCase() === 'yes')) {
                    opVal = 'yes';
                    rawVal = 'Yes';
                } else if (opVal === 'no' || ((opVal === 'equals' || opVal === '==' || opVal === '===') && rawVal.toLowerCase() === 'no')) {
                    opVal = 'no';
                    rawVal = 'No';
                } else if (opVal === 'equals' || opVal === '==' || opVal === '===') {
                    opVal = 'equals';
                } else {
                    opVal = 'exists';
                    rawVal = '';
                }

                return {
                    field: fieldVal,
                    operator: opVal,
                    value: rawVal
                };
            });
            setRules(mappedRules);
        }
        else {
            setOperator('&&');
            setRules([{ field: '', operator: 'exists', value: '' }]);
        }
    }, [variables]);

    useEffect(() => {
        if (isOpen) {
            const initialScope = tableTargetOptions?.selectedTarget || 'row';
            setTableScope(initialScope);
            const dataToLoad = (initialScope === 'column' && tableTargetOptions?.colRuleData)
                ? tableTargetOptions.colRuleData
                : ((initialScope === 'row' && tableTargetOptions?.rowRuleData) ? tableTargetOptions.rowRuleData : initialData);
            loadRuleData(dataToLoad);
        }
    }, [isOpen, initialData, tableTargetOptions, loadRuleData]);

    const handleScopeChange = (newScope) => {
        setTableScope(newScope);
        if (newScope === 'row') {
            loadRuleData(tableTargetOptions?.rowRuleData);
        } else if (newScope === 'column') {
            loadRuleData(tableTargetOptions?.colRuleData);
        }
    };

    if (!isOpen)
        return null;

    const handleAddRule = () => {
        setRules([...rules, { field: '', operator: 'exists', value: '' }]);
    };

    const handleRemoveRule = (index) => {
        if (rules.length > 1) {
            setRules(rules.filter((_, i) => i !== index));
        }
    };

    const updateRule = (index, key, val) => {
        const newRules = [...rules];
        if (key === 'operator') {
            const newOp = val;
            let newVal = newRules[index].value || '';
            if (newOp === 'yes') {
                newVal = 'Yes';
            } else if (newOp === 'no') {
                newVal = 'No';
            } else if (newOp === 'exists') {
                newVal = '';
            }
            newRules[index] = { ...newRules[index], operator: newOp, value: newVal };
        }
        else {
            newRules[index] = { ...newRules[index], [key]: val };
        }
        setRules(newRules);
    };

    const isValid = rules.every(c => {
        if (!c.field) return false;
        if (c.operator === 'equals' && (!c.value || !c.value.trim())) return false;
        return true;
    });

    const handleSave = () => {
        if (!isValid)
            return;
        const labelParts = rules.map((c) => {
            const selectedVariable = variables.find((v) => v.key === c.field || v.label === c.field);
            const variableLabel = (selectedVariable?.label ?? c.field).replace(/<[^>]+>/g, '').replace(/<\/?strong[^>]*>/gi, '').replace(/<\/?b[^>]*>/gi, '').replace(/\*\*/g, '').trim();
            const cleanVal = (c.value || '').replace(/<[^>]+>/g, '').replace(/<\/?strong[^>]*>/gi, '').replace(/<\/?b[^>]*>/gi, '').replace(/\*\*/g, '').trim();
            
            if (c.operator === 'exists') {
                return `<<${variableLabel}>>`;
            } else if (c.operator === 'yes') {
                return `<<${variableLabel}>> == Yes`;
            } else if (c.operator === 'no') {
                return `<<${variableLabel}>> == No`;
            } else if (c.operator === 'equals') {
                return cleanVal ? `<<${variableLabel}>> == ${cleanVal}` : `<<${variableLabel}>>`;
            }
            return `<<${variableLabel}>>`;
        });
        const joiner = operator === '||' || operator === 'OR' ? ' || ' : ' && ';
        const label = labelParts.join(joiner);
        onSave({
            operator: operator === '||' || operator === 'OR' ? '||' : '&&',
            rules: rules.map(c => {
                const selectedVariable = variables.find((v) => v.key === c.field);
                const variableLabel = selectedVariable?.label ?? c.field;
                let finalOp = 'exists';
                let finalVal = '';
                if (c.operator === 'yes') {
                    finalOp = 'equals';
                    finalVal = 'Yes';
                } else if (c.operator === 'no') {
                    finalOp = 'equals';
                    finalVal = 'No';
                } else if (c.operator === 'equals') {
                    finalOp = 'equals';
                    finalVal = c.value || '';
                }
                return {
                    field: c.field,
                    variable: variableLabel,
                    operator: finalOp,
                    value: finalVal
                };
            }),
            label,
            targetScope: tableTargetOptions ? tableScope : null,
        });
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (<div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="rule-modal" id="rule-modal" style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <div className="modal-title-row">
            <GitBranch size={20}/>
            <h3>{tableTargetOptions ? 'Table Condition' : 'Visibility Rule'}</h3>
          </div>
          <button type="button" className="modal-close" onClick={onClose} title="Close">
            <X size={20}/>
          </button>
        </div>

        <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <p className="modal-description">
            {tableTargetOptions 
              ? 'Define when this table row or column should appear in the generated document.'
              : 'Define when this block of text should appear in the generated document.'}
          </p>

          {tableTargetOptions && (
            <div className="table-condition-scope-card" style={{ marginBottom: '16px', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 14px', borderRadius: '8px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>
                Apply Table Condition To:
              </label>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px', fontWeight: 600, color: tableScope === 'row' ? '#7c3aed' : '#475569', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="tableConditionScope"
                    value="row"
                    checked={tableScope === 'row'}
                    onChange={() => handleScopeChange('row')}
                  />
                  <span>Entire Row</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px', fontWeight: 600, color: tableScope === 'column' ? '#16a34a' : '#475569', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="tableConditionScope"
                    value="column"
                    checked={tableScope === 'column'}
                    onChange={() => handleScopeChange('column')}
                  />
                  <span>Particular Column</span>
                </label>
              </div>
            </div>
          )}

          <datalist id="value-options">
            <option value="Yes"/>
            <option value="No"/>
            <option value="True"/>
            <option value="False"/>
          </datalist>

          {rules.length > 1 && (<div style={{ marginBottom: '16px', background: '#f3f4f6', padding: '12px', borderRadius: '8px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                Rule Combination Logic
              </label>
              <div style={{ display: 'flex', gap: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13.5px', cursor: 'pointer' }}>
                  <input type="radio" checked={operator === '&&' || operator === 'AND'} onChange={() => setOperator('&&')}/>
                  Match ALL rules (&&)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13.5px', cursor: 'pointer' }}>
                  <input type="radio" checked={operator === '||' || operator === 'OR'} onChange={() => setOperator('||')}/>
                  Match ANY rule (||)
                </label>
              </div>
            </div>)}

          {rules.map((rule, index) => {
            const needsValue = rule.operator === 'equals';
            return (<div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '12px', background: '#f9fafb', padding: '12px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                    {index === 0 ? 'Rule' : (operator === '||' || operator === 'OR' ? '|| (OR)' : '&& (AND)')}
                  </label>
                  <select
                    value={rule.field}
                    onChange={(e) => updateRule(index, 'field', e.target.value)}
                    className="modal-select"
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', boxSizing: 'border-box' }}
                  >
                    <option value="">Select variable...</option>
                    {sortedVariables.map((v) => (
                      <option key={v.key} value={v.key}>
                        {v.label}
                      </option>
                    ))}
                    {rule.field && !sortedVariables.some((v) => v.key === rule.field) && (
                      <option key={rule.field} value={rule.field}>
                        {rule.field}
                      </option>
                    )}
                  </select>
                </div>

                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                    Condition
                  </label>
                  <select
                    value={rule.operator}
                    onChange={(e) => updateRule(index, 'operator', e.target.value)}
                    className="modal-select"
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', boxSizing: 'border-box' }}
                  >
                    {CONDITION_OPERATORS.map((op) => (
                      <option key={op.key} value={op.key}>
                        {op.label}
                      </option>
                    ))}
                  </select>
                </div>

                {needsValue && (<div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                      Value
                    </label>
                    <input
                      type="text"
                      value={rule.value || ''}
                      onChange={(e) => updateRule(index, 'value', e.target.value)}
                      placeholder="Type value..."
                      list="value-options"
                      className="modal-input"
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', boxSizing: 'border-box' }}
                    />
                  </div>)}

                {rules.length > 1 && (<button type="button" onClick={() => handleRemoveRule(index)} style={{ padding: '8px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '2px' }} title="Remove rule">
                    <Trash2 size={18}/>
                  </button>)}
              </div>);
        })}

          <button type="button" onClick={handleAddRule} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#4f46e5', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, marginTop: '8px' }}>
            <Plus size={16}/> Add another rule
          </button>
        </div>

        <div className="modal-footer">
          <button type="button" className="modal-btn modal-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          
          <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
            {onRemove && (<button type="button" className="modal-btn" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', cursor: 'pointer', fontWeight: 600 }} onClick={() => onRemove(tableTargetOptions ? { targetScope: tableScope } : null)}>
                Remove Condition
              </button>)}
            <button type="button" className="modal-btn modal-btn-save" onClick={handleSave} disabled={!isValid}>
              Apply Rule
            </button>
          </div>
        </div>
      </div>
    </div>);
}
